import { useEffect, type ReactNode } from 'react';
import { startEmbers } from '../lib/emberEffect';
import { startSparks } from '../lib/sparkEffect';

// AmbientEffects — mounts the GG Anvil brand embers + click-spark canvases
// for as long as this subtree is mounted, and tears them down cleanly when
// it isn't.
//
// Why this exists: gg-anvil-embers.js / gg-anvil-sparks.js are standalone
// drop-in IIFEs written for static pages — they create a <canvas>, append it
// to document.body, and (embers) start an infinite rAF loop or (sparks)
// attach a page-wide pointerdown listener, with no exported teardown. That's
// fine for a page that loads once and runs forever. It is NOT fine in this
// client-routed SPA: naively injecting a <script> tag on mount doesn't undo
// any of that on unmount (removing a <script> tag does not undo what its
// IIFE already did), so navigating between two public pages would stack a
// second canvas + a second listener on top of the first, and navigating to
// an interior page wouldn't stop the first one's loop/listener either.
//
// The fix: lib/emberEffect.ts and lib/sparkEffect.ts are the same scripts
// adapted into startEmbers()/startSparks() functions that return a teardown
// closure (cancels the rAF loop, removes the listener, removes the canvas).
// This component calls them in a useEffect and returns the combined
// teardown as the cleanup — the standard React lifecycle for an external,
// non-React side effect (same pattern as AccentInjector.tsx).
//
// Mount once per public route via the route wrapper in App.tsx (NOT inside
// each page component) so the effect's mount/unmount is tied to react-router
// swapping the matched Route element, not to a page's internal loading/error
// branches. React Router unmounts the whole previous Route element and
// mounts a fresh one on every navigation between different route paths, so
// this guarantees: exactly one embers canvas + one sparks canvas while on a
// public page, and both fully torn down (loop cancelled, listener removed,
// canvas removed) the moment you navigate to a different route — including
// into the interior/authenticated app, and including React 18 StrictMode's
// dev-only double-invoke (mount → cleanup → mount), which this survives
// because a fresh start*() call always creates a brand-new canvas from a
// clean DOM.
export function AmbientEffects({ children }: { children: ReactNode }) {
  useEffect(() => {
    const stopEmbers = startEmbers();
    const stopSparks = startSparks();
    return () => {
      stopEmbers();
      stopSparks();
    };
  }, []);

  return <>{children}</>;
}
